// ============================================================================
// USE WORKFLOW STATUSES HOOK
// Fetches custom_statuses from work_item_number_sequences config
// for dynamic Kanban board columns per item type
// Date: January 20, 2026
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { authService } from '@/lib/authService';
import { pickSequenceRow } from '@/lib/work-items/sequenceScope';
import {
  ensureRequiredOperationalWorkflowStatuses,
  getDefaultAppointmentWorkflowStatuses,
  getDefaultOperationalWorkflowStatuses,
} from '@/lib/work-items/operationalStatusConfig';
import { getFinanceStatuses, isFinanceItemType } from '@/lib/work-items/financeStatusConfig';

// Default workflow statuses for operational item types (matching Kanban board columns)
const DEFAULT_OPERATIONAL_STATUSES = getDefaultOperationalWorkflowStatuses();

// Default workflow statuses for ticket item types
const DEFAULT_TICKET_STATUSES = [
  { key: 'open', label: 'Open', color: 'blue', description: 'New ticket' },
  { key: 'in_progress', label: 'In Progress', color: 'yellow', description: 'Being worked on' },
  { key: 'waiting', label: 'Waiting', color: 'orange', description: 'Awaiting response' },
  { key: 'resolved', label: 'Resolved', color: 'green', description: 'Issue resolved' },
  { key: 'closed', label: 'Closed', color: 'gray', description: 'Ticket closed' },
];

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
// Default workflow statuses for finance item types
const DEFAULT_FINANCE_STATUSES = [
  { key: 'draft', label: 'Draft', color: 'gray', description: 'Not yet sent' },
  { key: 'sent', label: 'Sent', color: 'blue', description: 'Sent to customer' },
  { key: 'viewed', label: 'Viewed', color: 'purple', description: 'Customer has viewed' },
  { key: 'approved', label: 'Approved', color: 'green', description: 'Accepted/approved' },
  { key: 'rejected', label: 'Rejected', color: 'red', description: 'Declined' },
];

// Map item types to their default statuses
const DEFAULT_STATUSES_BY_TYPE: Record<string, WorkflowStatus[]> = {
  job: DEFAULT_OPERATIONAL_STATUSES,
  task: DEFAULT_OPERATIONAL_STATUSES,
  project: DEFAULT_OPERATIONAL_STATUSES,
  appointment: getDefaultAppointmentWorkflowStatuses(),
  ticket: DEFAULT_TICKET_STATUSES,
  asset: [
    { key: 'available', label: 'Available', color: 'green', description: 'Ready for use' },
    { key: 'in_use', label: 'In Use', color: 'blue', description: 'Currently assigned' },
    { key: 'maintenance', label: 'Maintenance', color: 'yellow', description: 'Under maintenance' },
    { key: 'retired', label: 'Retired', color: 'gray', description: 'No longer in service' },
  ],
  invoice: DEFAULT_FINANCE_STATUSES,
  quote: DEFAULT_FINANCE_STATUSES,
  supplier_invoice: [
    { key: 'draft', label: 'Draft', color: 'gray', description: 'Not yet processed' },
    { key: 'pending', label: 'Pending', color: 'yellow', description: 'Awaiting payment' },
    { key: 'paid', label: 'Paid', color: 'green', description: 'Payment complete' },
    { key: 'cancelled', label: 'Cancelled', color: 'red', description: 'Supplier invoice cancelled' },
  ],
  audit: [
    { key: 'scheduled', label: 'Scheduled', color: 'blue', description: 'Audit scheduled' },
    { key: 'in_progress', label: 'In Progress', color: 'yellow', description: 'Audit underway' },
    { key: 'passed', label: 'Passed', color: 'green', description: 'Audit passed' },
    { key: 'failed', label: 'Failed', color: 'red', description: 'Audit failed' },
  ],
  inspection: [
    { key: 'scheduled', label: 'Scheduled', color: 'blue', description: 'Inspection scheduled' },
    { key: 'in_progress', label: 'In Progress', color: 'yellow', description: 'Inspection underway' },
    { key: 'passed', label: 'Passed', color: 'green', description: 'Passed inspection' },
    { key: 'failed', label: 'Failed', color: 'red', description: 'Failed inspection' },
  ],
};

export interface WorkflowStatus {
  key: string;
  label: string;
  color: string;
  description?: string;
  isStart?: boolean;
  isEnd?: boolean;
  isSystem?: boolean;
  /** Which statuses are allowed to transition TO this status. Mirrors useJobConfig WorkflowStatus. */
  allowed_from?: string[];
}

interface SequenceConfig {
  item_type: string;
  /** 'default' (or missing pre-migration) = client-wide row; other values are
   * workflow keys carrying scoped config overrides. */
  workflow_context?: string;
  config?: {
    visible_tabs?: string[];
    show_in_type_filter?: boolean;
    custom_statuses?: WorkflowStatus[];
  };
}

interface UseWorkflowStatusesResult {
  workflowStatuses: WorkflowStatus[];
  hasCustomStatuses: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// Cache for workflow statuses to avoid repeated API calls
const statusCache: Map<string, { statuses: WorkflowStatus[]; timestamp: number }> = new Map();
const CACHE_TTL = 60000; // 1 minute cache

/**
 * Hook to fetch custom workflow statuses for a specific item type
 * Falls back to defaults if no custom config exists
 * 
 * @param itemType - The work item type (e.g., 'job', 'ticket', 'project')
 * @param itemTypes - Optional array of item types to merge statuses from
 * @param workflowContext - Optional workflow scope ('visitor-management', …);
 *   a scoped sequence row for that workflow wins over the 'default' row.
 */
export function useWorkflowStatuses(
  itemType?: string,
  itemTypes?: string[],
  workflowContext?: string | null
): UseWorkflowStatusesResult {
  const [allSequences, setAllSequences] = useState<SequenceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      const cacheKey = 'all_sequences';
      const cached = statusCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        // Use cached data - but we need sequences, not statuses
        // Actually, let's store sequences in cache
      }

      const token = authService.getTokens()?.access_token;
      if (!token) {
        // Fallback to defaults if not authenticated
        setAllSequences([]);
        return;
      }

      const response = await fetch('/api/v1/admin/work-item-sequences', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch workflow configuration');
      }

      const data = await response.json();
      const sequences: SequenceConfig[] = data.data || [];

      // Load transitions from work_item_workflow_transitions table
      const transitionsResponse = await fetch('/api/v1/work-items/transitions', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const transitionsData = await transitionsResponse.json();
      const transitions = transitionsData.success ? transitionsData.data : [];

      // Build allowed_from map from transitions
      const allowedFromMap = new Map<string, string[]>();
      transitions.forEach((transition: { from_status: string; to_status: string }) => {
        if (!allowedFromMap.has(transition.to_status)) {
          allowedFromMap.set(transition.to_status, []);
        }
        allowedFromMap.get(transition.to_status)!.push(transition.from_status);
      });

      // Add allowed_from to custom_statuses in sequences
      const sequencesWithAllowedFrom = sequences.map((sequence) => {
        if (!sequence.config?.custom_statuses) {
          return sequence;
        }
        return {
          ...sequence,
          config: {
            ...sequence.config,
            custom_statuses: sequence.config.custom_statuses.map((status) => ({
              ...status,
              allowed_from: allowedFromMap.has(status.key) ? allowedFromMap.get(status.key) : undefined,
            })),
          },
        };
      });

      setAllSequences(sequencesWithAllowedFrom);

    } catch (err: unknown) {
      console.error('[useWorkflowStatuses] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workflow configuration');
      setAllSequences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Compute workflow statuses based on item type(s)
  const resolvedWorkflowConfig = useMemo(() => {
    // If multiple item types, we need to merge their statuses intelligently
    const typesToCheck = itemTypes?.length ? itemTypes : (itemType ? [itemType] : []);
    
    if (typesToCheck.length === 0) {
      // No specific type - return operational defaults
      return {
        workflowStatuses: DEFAULT_OPERATIONAL_STATUSES,
        hasCustomStatuses: false,
      };
    }

    // For multiple types, find the primary type's statuses
    // Priority: job > project > task > first type
    const primaryType = ['job', 'project', 'task'].find(t => typesToCheck.includes(t)) || typesToCheck[0];
    
    // Look for custom config — workflow-scoped row wins over 'default'
    const sequence = pickSequenceRow(allSequences, primaryType, workflowContext);
    const hasCustomStatuses = !!(sequence?.config?.custom_statuses && sequence.config.custom_statuses.length > 0);
    
    if (hasCustomStatuses) {
      return {
        workflowStatuses: ensureRequiredOperationalWorkflowStatuses(sequence!.config!.custom_statuses!, primaryType),
        hasCustomStatuses: true,
      };
    }
    
    // Try SSOT first for finance types
    if (isFinanceItemType(primaryType)) {
      const ssotStatuses = getFinanceStatuses(primaryType);
      if (ssotStatuses.length > 0) {
        return {
          workflowStatuses: ssotStatuses.map(s => ({
            key: s.key,
            label: s.label,
            color: s.color,
            description: s.description,
            isSystem: s.is_system,
            allowed_from: s.allowed_from,
          })),
          hasCustomStatuses: false,
        };
      }
    }

    // Fall back to defaults for this type
    return {
      workflowStatuses: ensureRequiredOperationalWorkflowStatuses(
        DEFAULT_STATUSES_BY_TYPE[primaryType] || DEFAULT_OPERATIONAL_STATUSES,
        primaryType,
      ),
      hasCustomStatuses: false,
    };
  }, [itemType, itemTypes, workflowContext, allSequences]);

  return {
    workflowStatuses: resolvedWorkflowConfig.workflowStatuses,
    hasCustomStatuses: resolvedWorkflowConfig.hasCustomStatuses,
    loading,
    error,
    refresh: fetchConfig
  };
}

/**
 * Get default statuses for an item type (synchronous, no API call)
 */
export function getDefaultStatusesForType(itemType: string): WorkflowStatus[] {
  return ensureRequiredOperationalWorkflowStatuses(
    DEFAULT_STATUSES_BY_TYPE[itemType] || DEFAULT_OPERATIONAL_STATUSES,
    itemType,
  );
}

export { DEFAULT_OPERATIONAL_STATUSES, DEFAULT_STATUSES_BY_TYPE };
